import { useState } from 'react';

import {
  Button,
  ChakraBadge,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  Modal,
  RadioGroup,
} from '@equip.health/ui';

import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { getPreferredFullName } from '~/lib/util/patient.util';
import { useGetAppUsersQuery } from '~/store/services/appUsers';

const { h6, label, labelBold, captionBold } = EquipUITheme.typography;
const { primary, neutral } = EquipUIFireflyV2Theme.colors;

const TOP_LEVEL_ID = 'survey-respondent';

export interface SurveyRespondentModalProps {
  data: {
    isLoading: boolean;
    onConfirm: ({
      toUserEmail,
      toUserFirstName,
      toUserLastName,
      toUserPhone,
    }: SurveyRespondentProps) => void;
    toUserEmail: string;
    toUserFirstName: string;
    toUserLastName: string;
    toUserPhone: string;
    hasEquipAccount?: boolean;
  };
  isOpen: boolean;
  onDismiss: () => void;
}
const SurveyRespondentModal = (props: SurveyRespondentModalProps) => {
  const { currentPatientId } = useGlobalContext();
  const { data: appUsers = [] } = useGetAppUsersQuery(
    { patientId: currentPatientId, includeGeneral: true },
    {
      skip: !currentPatientId,
    },
  );
  const { isOpen, data, onDismiss } = props;
  const { isLoading, onConfirm } = data;
  const [selectedContact, setSelectedContact] = useState<string>(null);
  const componentPortalContext = useComponentPortalContext();
  const { setComponentVisibility } = componentPortalContext;

  const getOptions = () => {
    return appUsers.map(
      ({
        chosenName,
        externalId,
        firstName,
        isGuarantor,
        isPrimaryContact,
        lastName,
        relationToPatient,
      }) => ({
        label: (
          <ChakraFlex
            alignItems="flex-start"
            direction="column"
            id={`${TOP_LEVEL_ID}__radio-option-${externalId}`}
            padding="16px"
          >
            <ChakraText
              color="neutral.primary"
              id={`${TOP_LEVEL_ID}__radio-option-name-${externalId}`}
              {...h6}
            >
              {getPreferredFullName(chosenName, firstName, lastName)}
            </ChakraText>
            <ChakraText
              color="neutral.secondary"
              id={`${TOP_LEVEL_ID}__radio-option-relation-${externalId}`}
              {...label}
            >
              {relationToPatient}
              {isPrimaryContact && (
                <ChakraBadge
                  backgroundColor="primary.100"
                  borderRadius="100px"
                  color="neutral.background.primary"
                  height="18px"
                  id={`${TOP_LEVEL_ID}__primary-badge-${externalId}`}
                  marginBottom="4px"
                  marginLeft="4px"
                  padding="2px 7px"
                  textTransform="none"
                >
                  <ChakraText
                    id={`${TOP_LEVEL_ID}__primary-label-${externalId}`}
                    {...captionBold}
                  >
                    Primary
                  </ChakraText>
                </ChakraBadge>
              )}
              {isGuarantor && (
                <ChakraBadge
                  backgroundColor="secondary.50"
                  borderRadius="100px"
                  color="neutral.background.primary"
                  height="18px"
                  id={`${TOP_LEVEL_ID}__guarantor-badge-${externalId}`}
                  marginBottom="4px"
                  marginLeft="4px"
                  padding="2px 7px"
                  textTransform="none"
                >
                  <ChakraText
                    id={`${TOP_LEVEL_ID}__guarantor-label-${externalId}`}
                    {...captionBold}
                  >
                    Guarantor
                  </ChakraText>
                </ChakraBadge>
              )}
            </ChakraText>
          </ChakraFlex>
        ),
        value: externalId,
      }),
    );
  };

  const onConfirmClick = () => {
    const selectedAccount = appUsers.find(
      (each) => each.externalId === selectedContact,
    );

    setComponentVisibility({
      ...props,
      component: PortalComponent.ADD_EDIT_SURVEY_RESPONDENT_ACCORDION,
      data,
    });

    onConfirm({
      assigneeExternalId: selectedAccount.externalId,
      toUserEmail: selectedAccount?.email,
      toUserFirstName: selectedAccount?.firstName,
      toUserLastName: selectedAccount?.lastName,
      toUserPhone: selectedAccount?.phoneNumber,
    });
  };

  return (
    <Modal
      footerContent={
        <ChakraFlex
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Button
            {...labelBold}
            id={`${TOP_LEVEL_ID}__cancel-btn`}
            onClick={onDismiss}
            variant="transparent"
          >
            <ChakraText {...labelBold} color="primary.100">
              Cancel
            </ChakraText>
          </Button>

          <Button
            {...labelBold}
            id={`${TOP_LEVEL_ID}__submit-btn`}
            isDisabled={selectedContact === null || isLoading}
            isLoading={isLoading}
            onClick={onConfirmClick}
            padding="13px 12px 12px"
            primary
            type="button"
            variant="solid"
          >
            <ChakraText {...labelBold}>Send assessment</ChakraText>
          </Button>
        </ChakraFlex>
      }
      id={`${TOP_LEVEL_ID}__modal`}
      isOpen={isOpen}
      onClose={onDismiss}
      size="md"
      title={
        <ChakraText id={`${TOP_LEVEL_ID}__title`} {...h6}>
          Select assignee
        </ChakraText>
      }
    >
      <ChakraBox
        {...label}
        id={`${TOP_LEVEL_ID}__sub-title`}
        padding="0px 0px 8px"
      >
        <>
          <ChakraText
            color="neutral.secondary"
            id={`${TOP_LEVEL_ID}__sub-title-1`}
          >
            Choose the contact you want to assign this assessment
          </ChakraText>
          <ChakraText
            color="neutral.secondary"
            id={`${TOP_LEVEL_ID}__sub-title-2`}
            marginBottom="8px"
            marginTop="16px"
          >
            Contacts with application access
            <ChakraBadge
              {...captionBold}
              backgroundColor="neutral.line"
              borderRadius="100px"
              fontColor="neutral.secondary"
              fontSize="10px"
              height="16px"
              id={`${TOP_LEVEL_ID}__num-of-contacts`}
              marginLeft="10px"
              padding="0px 8px"
            >
              {appUsers.length}
            </ChakraBadge>
          </ChakraText>
          {appUsers.length > 0 ? (
            <RadioGroup
              direction="column"
              id={`${TOP_LEVEL_ID}__radio-group`}
              onChange={setSelectedContact}
              options={getOptions()}
              radioIconMarginRight="16px"
              radioOptionActiveBackground="primary.10"
              radioOptionActiveBorderColor={`2px solid ${primary[10]}`}
              radioOptionBackground="white"
              radioOptionBorder={`1px solid ${neutral.line}`}
              radioOptionBorderRadius="16px"
              radioOptionBoxBorderRadius="16px"
              radioOptionPadding="16px"
              radioSize="lg"
              stackBorder="none"
              stackBorderRadius="0"
              stackSpacing="8px"
              value={selectedContact}
            />
          ) : (
            <ChakraText
              {...h6}
              id={`${TOP_LEVEL_ID}__no-contacts-found-label`}
              margin="24px 0px 0px 48px"
            >
              No accounts found
            </ChakraText>
          )}
        </>
      </ChakraBox>
    </Modal>
  );
};

export default SurveyRespondentModal;
